import { useController, useFormContext, useWatch } from 'react-hook-form';

import { ComponentFieldDisplay, MeasurementFieldType, UnitConverter } from 'models';

import { useComponentBulkFields } from 'components/component-bulk-editor/ComponentBulkFieldsContext';

import { useMultiUnitMeasurementRelay } from './useMultiUnitMeasurementRelay';

type Props<TUnit extends string, TBaseUnit extends TUnit> = {
    name: string;
    fields: readonly MeasurementFieldType[];
    converter: UnitConverter<TUnit, TBaseUnit>;
    defaultUnit?: TUnit;
    displayUnits?: TUnit[];
};

export type FieldsRecord = Record<
    MeasurementFieldType,
    {
        value: string;
        onChange: (value: string) => void;
        hasDifferingValues: boolean;
        display: ComponentFieldDisplay;
        error: string | undefined;
        isDirty?: boolean;
    }
>;

export type MultiUnitMeasurementController = {
    unit: string;
    cycleUnit?: () => void;
    convertValue: (value: number) => string;
    fields: FieldsRecord;
};

const useMultiUnitMeasurementController = <TUnit extends string, TBaseUnit extends TUnit>({
    name,
    fields,
    converter,
    defaultUnit = converter.baseUnit,
    displayUnits = [...converter.getDisplayUnits()],
}: Props<TUnit, TBaseUnit>): MultiUnitMeasurementController => {
    const {
        field: { value: fieldValues },
        fieldState: { error, isDirty },
    } = useController({
        name,
        defaultValue: Object.fromEntries(fields.map((field) => [field, null])),
    });

    const { setValue } = useFormContext();
    const { overrides, diff } = useComponentBulkFields();
    const componentMeta = useWatch({ name: `metadata.${name}` });

    const handleRelayChange = (newValue: Record<MeasurementFieldType, number | null>) => {
        setValue(name, newValue, { shouldDirty: true });
    };

    const relay = useMultiUnitMeasurementRelay({
        fields,
        value: fieldValues,
        onChange: handleRelayChange,
        converter,
        defaultUnit,
        displayUnits,
    });

    const fieldsRecord: FieldsRecord = Object.fromEntries(
        fields.map((field) => {
            const fieldPath = `${name}.${field}`;
            return [
                field,
                {
                    value: relay.fields[field].value,
                    onChange: relay.fields[field].onChange,
                    hasDifferingValues: diff.includes(fieldPath) && !overrides.includes(fieldPath),
                    display: componentMeta?.[field]?.display ?? ComponentFieldDisplay.NORMAL,
                    error: error?.message,
                    isDirty,
                },
            ];
        }),
    ) as FieldsRecord;

    return {
        unit: relay.unit,
        cycleUnit: relay.cycleUnit,
        convertValue: relay.convertValue,
        fields: fieldsRecord,
    };
};

export { useMultiUnitMeasurementController };
